import React, { useState, useEffect } from 'react';
import { locationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MapPin, Phone, Building, Search } from 'lucide-react';
import DataPagination from '../components/DataPagination';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    fetchLocations();
  }, [currentPage, pageSize, searchQuery]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (searchQuery) params.search = searchQuery;
      const response = await locationsAPI.getAll(params);
      const paginatedData = response.data;
      setLocations(paginatedData.items || []);
      setTotalItems(paginatedData.total || 0);
      setTotalPages(paginatedData.total_pages || 0);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await locationsAPI.update(editingLocation.id, formData);
        toast.success('Location updated successfully');
      } else {
        await locationsAPI.create(formData);
        toast.success('Location created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await locationsAPI.delete(id);
      toast.success('Location deleted');
      fetchLocations();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      is_active: true
    });
    setEditingLocation(null);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      phone: location.phone,
      is_active: location.is_active
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Locations</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your clinic locations</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="pl-9 w-56 bg-white border-slate-200"
              data-testid="location-search"
            />
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="add-location-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
                <DialogDescription>
                  {editingLocation ? 'Update clinic location details' : 'Add a new clinic location'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">Location Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Main Clinic, Downtown Branch"
                      required
                      className="mt-1.5"
                      data-testid="location-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address" className="text-slate-700">Address *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Full address"
                      required
                      className="mt-1.5"
                    data-testid="location-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Contact number"
                    required
                    className="mt-1.5"
                    data-testid="location-phone-input"
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="text-slate-700">Active</Label>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="save-location-btn">
                  {editingLocation ? 'Update Location' : 'Create Location'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Locations Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : locations.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-16">
            <div className="empty-state">
              <MapPin className="empty-state-icon" />
              <p className="empty-state-title">No locations found</p>
              <p className="empty-state-text">{searchQuery ? 'Try a different search term' : 'Add your first clinic location to get started'}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map((location) => (
            <Card key={location.id} className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow" data-testid={`location-card-${location.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <Building className="w-6 h-6 text-slate-700" />
                  </div>
                  <span className={location.is_active ? 'badge-success' : 'badge-error'}>
                    {location.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{location.name}</h3>
                
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" />
                    <span>{location.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{location.phone}</span>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => openEditDialog(location)}
                    data-testid={`edit-location-${location.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-slate-200 text-rose-600 hover:bg-rose-50"
                    onClick={() => handleDelete(location.id)}
                    data-testid={`delete-location-${location.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {!loading && locations.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </div>
  );
};

export default Locations;
